import isEmpty from 'lodash/isEmpty'
import { createValidationError, email as validEmail, isFilterInSentence, required } from 'labguard-cdk'
import { dutyContactDeviceFields } from 'src/entities'

export const DEFAULT_USER_NAME_I18N_ID = 'WEB_PERSON_NAME'

/**
 * A user name depends on the language (e.g. `firstname` followed by `lastname` for Western people, but the opposite
 * for Eastern people). That's why we rely on the i18n message file to generate a user name.
 * @param intl react-intl service.
 * @param user Object with a `firstname` and a `lastname` properties.
 * @return {String} localized user name.
 */
export const formatUserName = (intl, user) => user && intl.formatMessage({ id: DEFAULT_USER_NAME_I18N_ID }, user)

/**
 * TableWithFilter predicate to filter on a formatted username.
 * @param filter The table filter value
 * @param user The user object
 * @param intl The intl service
 * @return {Boolean} True if the formatted username matches the given filter value, false otherwise.
 */
export const filterUserName = ({ filter, rowData: user, intl }) =>
  isFilterInSentence(filter, formatUserName(intl, user))

/**
 * Validates a user form object
 * @param lastname The user's lastname
 * @param firstname The user's firstname
 * @param email The user's email
 * @param selectedDutyCall The user's checked call number
 * @param selectedDutySMS The user's checked sms number
 * @param accessLevel The user's access level
 * @param status The user's status
 * @param user The rest of the user object
 * @return {Object} An object with the field names which are in error as keys and the corresponding error messages
 * as values. The object will be empty if there was no error.
 */
export const validateUser = ({
  username,
  lastname,
  firstname,
  email,
  selectedDutyCall,
  selectedDutySMS,
  accessLevel,
  status,
  ...user
}) => ({
  username: required(username),
  lastname: required(lastname),
  firstname: required(firstname),
  email: required(email) || validEmail(email),
  selectedDutyCall: isEmpty(selectedDutyCall) ? createValidationError('WEB_DUTY_VOICE_CALL_VALIDATOR') : undefined,
  selectedDutySMS: isEmpty(selectedDutySMS) ? createValidationError('WEB_DUTY_SMS_VALIDATOR') : undefined,
  ...(selectedDutyCall && {
    [dutyContactDeviceFields[selectedDutyCall]]: required(user[dutyContactDeviceFields[selectedDutyCall]]),
  }),
  ...(selectedDutySMS && {
    [dutyContactDeviceFields[selectedDutySMS]]: required(user[dutyContactDeviceFields[selectedDutySMS]]),
  }),
  accessLevel: required(accessLevel),
  status: required(status),
})

/**
 * Validate that two passwords matches
 * @param password The user's password
 * @param confirmPassword The user's confirmed password
 * @return A formated and internationalized validation error.
 */
export const passwordMatch = (password, confirmPassword) =>
  password !== confirmPassword ? createValidationError('WEB_PASSWORDS_DONT_MATCH') : undefined

/**
 * Validates a created user form object
 * @param password The user's password
 * @param confirmPassword Confirmation of the user's password
 * @param user The rest of the user object
 * @return {Object} An object with the field names which are in error as keys and the corresponding error messages
 * as values. The object will be empty if there was no error.
 */
export const validateCreateUser = ({ password, confirmPassword, ...user }) => ({
  password: required(password),
  confirmPassword: required(password) || passwordMatch(password, confirmPassword),
  ...validateUser(user),
})

/**
 * Converts a user object to a GraphQL UserInput.
 * @param id The user id
 * @param user The user
 * @returns {*} The user GraphQL input.
 */
export const asUserInput = ({ id, ...user }) => user

/**
 * Validates a password form object
 * @param newPassword The user's new password
 * @param confirmNewPassword The user's new password confirmation
 * @return {Object} An object with the field names which are in error as keys and the corresponding error messages
 * as values. The object will be empty if there was no error.
 */
export const validatePassword = (
  { username, password, newPassword, confirmNewPassword },
  { providePassword, provideUsername }
) => ({
  username: provideUsername && required(username),
  password: providePassword && required(password),
  newPassword: required(newPassword),
  confirmNewPassword: required(confirmNewPassword) || passwordMatch(newPassword, confirmNewPassword),
})
